import { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Load all client profiles for an instructor's organisation,
 * enriched with last activity date and progression level stub.
 *
 * @param {string|null} instructorId
 * @param {string|null} orgId
 * @returns {{ clients: object[], loading: boolean, error: string|null }}
 */
export function useInstructorClients(instructorId, orgId) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    async function load() {
      try {
        const ref = collection(db, 'clientProfiles');
        const q = query(ref, where('organizationId', '==', orgId));
        const snap = await getDocs(q);

        if (cancelled) return;

        const list = snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            userId: data.userId || '',
            displayName: data.displayName || data.name || data.email || '',
            email: data.email || '',
            sport: data.sport || '',
            organizationId: data.organizationId || '',
            instructorId: data.instructorId || '',
            instructorAuthUserId: data.instructorAuthUserId || '',
            lastActivityAt: data.lastActivityAt || data.updatedAt || data.createdAt || null,
            avatarUrl: data.avatarUrl || null,
            initials: buildInitials(data.displayName || data.name || data.email || ''),
          };
        });

        // Pass 58C — SECURITY: this is the instructor's "My Clients". It must be scoped to the logged-in
        // instructor, NOT the whole org (that was a cross-instructor PII leak — every instructor saw every
        // client's name/email). Match the client's instructorId against the instructor's identity in EITHER
        // canonical form (business userId `seed_instructor_*` OR the linked Firebase Auth UID). With no
        // instructor identity we show nothing rather than leak the org roster. (Org-wide client management
        // is the separate, permission-gated org Clients workspace.)
        const scoped = instructorId
          ? list.filter((c) => c.instructorId === instructorId || c.instructorAuthUserId === instructorId)
          : [];

        // Sort: most recent activity first
        scoped.sort((a, b) => {
          const da = toMs(a.lastActivityAt);
          const db = toMs(b.lastActivityAt);
          return db - da;
        });

        setClients(scoped);
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Failed to load clients');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [orgId, instructorId]);

  return { clients, loading, error };
}

function buildInitials(name) {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function toMs(ts) {
  if (!ts) return 0;
  if (ts.toDate) return ts.toDate().getTime();
  if (ts instanceof Date) return ts.getTime();
  return new Date(ts).getTime() || 0;
}
